// ============================================================================
// BESPOKIAN Pharmacist Cockpit - Auth Context
// Role-based access: PHARMACIST and TECHNICIAN only
// ============================================================================

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { api } from '@/api/client';
import type { User } from '@/types';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (role: 'PHARMACIST' | 'TECHNICIAN') => boolean;
  isPharmacist: boolean;
  isTechnician: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
  });

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setState({ user: null, isLoading: false, isAuthenticated: false });
        return;
      }

      try {
        const user = await api.get<User>('/auth/me');
        
        // Validate user has correct role
        if (user.role !== 'PHARMACIST' && user.role !== 'TECHNICIAN') {
          throw new Error('Unauthorized role');
        }

        setState({ user, isLoading: false, isAuthenticated: true });
      } catch {
        localStorage.removeItem('accessToken');
        setState({ user: null, isLoading: false, isAuthenticated: false });
      }
    };

    checkAuth();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const response = await api.post<{ accessToken: string; refreshToken: string; user: User }>(
        '/auth/login',
        { email, password },
      );

      // Validate role - only PHARMACIST and TECHNICIAN allowed
      if (
        response.user.role !== 'PHARMACIST' &&
        response.user.role !== 'TECHNICIAN'
      ) {
        throw new Error('Access denied. Pharmacist Cockpit is for pharmacy staff only.');
      }

      api.setToken(response.accessToken);
      setState({
        user: response.user,
        isLoading: false,
        isAuthenticated: true,
      });
    } catch (error) {
      setState({ user: null, isLoading: false, isAuthenticated: false });
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      api.setToken(null);
      setState({ user: null, isLoading: false, isAuthenticated: false });
    }
  }, []);

  const hasRole = useCallback(
    (role: 'PHARMACIST' | 'TECHNICIAN') => {
      return state.user?.role === role;
    },
    [state.user]
  );

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    hasRole,
    isPharmacist: state.user?.role === 'PHARMACIST',
    isTechnician: state.user?.role === 'TECHNICIAN',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Guard component for protected routes
interface RequireAuthProps {
  children: ReactNode;
  requiredRole?: 'PHARMACIST' | 'TECHNICIAN';
}

export function RequireAuth({ children, requiredRole }: RequireAuthProps) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pharmacy-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    window.location.href = '/login';
    return null;
  }

  if (requiredRole && user?.role !== requiredRole) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
          <p className="mt-2 text-gray-600">
            This feature requires {requiredRole} access.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
